import superagent from "superagent";
import { apiUrl } from "../common/apiUrl";

/**
 * @param {string} email
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<object>}
 */
export async function resolveEmail(email, options = {}) {
  const req = superagent
    .post(apiUrl("/auth/sso/resolve-email"))
    .send({ email: email.trim().toLowerCase() })
    .set("Accept", "application/json");

  const { signal } = options;
  if (signal) {
    if (signal.aborted) {
      const err = new Error("Aborted");
      err.name = "AbortError";
      throw err;
    }
    signal.addEventListener(
      "abort",
      () => {
        req.abort();
      },
      { once: true }
    );
  }

  const res = await req;
  return res.body;
}

/**
 * Prefer API-provided login_hint (Users.Email) then fall back to the typed login id.
 * @param {object | null | undefined} resolved
 * @param {string} loginId
 * @returns {string}
 */
export function resolveOidcLoginHint(resolved, loginId) {
  const fromApi = String(resolved?.login_hint || "")
    .trim()
    .toLowerCase();
  if (fromApi) return fromApi;
  return String(loginId || "")
    .trim()
    .toLowerCase();
}

/**
 * Build OIDC start URL (browser navigates here; API returns 302 to IdP).
 * @param {string} userPattern
 * @param {number} providerId
 * @param {string} loginId - MSuite UserName typed on the form (bound server-side)
 * @param {string} [loginHint] - Preferred IdP hint (email when available)
 */
export function buildOidcStartUrl(userPattern, providerId, loginId, loginHint) {
  const boundLoginId = String(loginId || "")
    .trim()
    .toLowerCase();
  const params = new URLSearchParams({
    user_pattern: userPattern,
    provider_id: String(providerId),
    login_id: boundLoginId,
    return_path: "/",
  });
  const hint = String(loginHint || boundLoginId)
    .trim()
    .toLowerCase();
  if (hint) {
    params.set("login_hint", hint);
  }
  return `${apiUrl("/auth/sso/oidc/start")}?${params.toString()}`;
}

/**
 * Build SAML start URL (browser navigates here; API returns redirect to IdP).
 * @param {string} userPattern
 * @param {number} providerId
 */
export function buildSamlStartUrl(userPattern, providerId) {
  const params = new URLSearchParams({
    user_pattern: userPattern,
    provider_id: String(providerId),
    return_path: "/",
  });
  return `${apiUrl("/auth/sso/saml/start")}?${params.toString()}`;
}

/**
 * Build SSO start URL for the configured protocol.
 * @param {string} userPattern
 * @param {number} providerId
 * @param {'oidc' | 'saml'} [protocol]
 * @param {string} [loginId]
 * @param {string} [loginHint]
 */
export function buildSsoStartUrl(
  userPattern,
  providerId,
  protocol = "oidc",
  loginId,
  loginHint
) {
  if (protocol === "saml") {
    return buildSamlStartUrl(userPattern, providerId);
  }
  return buildOidcStartUrl(userPattern, providerId, loginId, loginHint);
}

/**
 * Password login via Basic auth. Username is the work email.
 * @param {string} email
 * @param {string} password
 * @param {string} [breakGlassToken]
 * @returns {Promise<string>} JWT
 */
export async function passwordLogin(email, password, breakGlassToken) {
  let req = superagent
    .get(apiUrl("/auth/user"))
    .auth(email.trim().toLowerCase(), password)
    .set("Accept", "application/json");

  if (breakGlassToken && breakGlassToken.trim()) {
    req = req.set("X-Break-Glass", breakGlassToken.trim());
  }

  const res = await req;
  return res.text.replace(/"/g, "");
}

export const RESOLVE_GENERIC_ERROR =
  "Unable to sign in with this email. Check the address or contact your administrator.";

/**
 * @param {unknown} value
 * @returns {string}
 */
function errorMessageText(value) {
  if (typeof value === "string") {
    return value.trim();
  }

  if (Array.isArray(value)) {
    return value.map(errorMessageText).filter(Boolean).join(" ");
  }

  if (value && typeof value === "object") {
    if (typeof value.message === "string") {
      return value.message.trim();
    }
    if (value.error && typeof value.error === "object") {
      const nested = errorMessageText(value.error);
      if (nested) return nested;
    }
    if (typeof value.error === "string") {
      return value.error.trim();
    }
  }

  return "";
}

/**
 * True when SSO discovery is not deployed or unreachable (e.g. msuite_api pre-M3).
 * @param {unknown} err
 * @returns {boolean}
 */
export function isResolveUnavailableError(err) {
  const status = err?.status;
  // 404: endpoint not deployed yet, or legacy API that rejected unknown email domains.
  return (
    status === 401 ||
    status === 404 ||
    status === 405 ||
    status === 502 ||
    status === 503
  );
}

/**
 * @param {unknown} err
 * @returns {string}
 */
export function formatSsoError(err) {
  if (!err) {
    return "Something went wrong. Please try again.";
  }

  const status = err.status;
  const body = err.response?.body;
  const code = body?.code;
  const message =
    errorMessageText(body?.message) ||
    errorMessageText(body?.error) ||
    errorMessageText(body) ||
    errorMessageText(err.message);

  if (status === 401 || status === 404 || status === 400) {
    return RESOLVE_GENERIC_ERROR;
  }

  if (status === 403 && code === "PASSWORD_RESET_REQUIRED") {
    return (
      message ||
      "Password reset required. Use the link from your reset email, or Forgot password, to set a new password."
    );
  }

  if (status === 403) {
    return (
      message ||
      "Sign-in with password is not available. Use your organization sign-in or contact your administrator."
    );
  }

  if (status === 429) {
    return "Too many attempts. Please wait a moment and try again.";
  }

  if (message && !message.includes("Not Found")) {
    return message;
  }

  return "Something went wrong. Please try again.";
}
